import {useEffect, useState} from "react";
import {useParams, Link} from "react-router-dom";
import {useEditor, EditorContent} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {getFromApi, postToApi, deleteFromApi, patchToApi} from "../../api/auth";
import ConfirmButton from "../../components/shared/ConfirmButton";
import InlineRename from "../../components/shared/InlineRename";
import LinkedTodo from "../../components/notes/LinkedTodo";

function MenuBar({editor}) {
    if (!editor) return null;
    const btn = (label, action, active) => (
        <button type="button" className={`tiptap-btn${active?" active":""}`} onMouseDown={e => { e.preventDefault(); action(); }}>{label}</button>
    );
    return (
        <div className="tiptap-toolbar">
            {btn("B",  () => editor.chain().focus().toggleBold().run(),   editor.isActive("bold"))}
            {btn("I",  () => editor.chain().focus().toggleItalic().run(), editor.isActive("italic"))}
            {btn("• List", () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"))}
            {btn("1. List", () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"))}
            {btn("↩", () => editor.chain().focus().undo().run(), false)}
            {btn("↪", () => editor.chain().focus().redo().run(), false)}
        </div>
    );
}

export default function ProjectLogView() {
    const {id} = useParams();
    const [project, setProject]       = useState(null);
    const [linkedTodo, setLinkedTodo] = useState(null);
    const [allTodoGroups, setAllTodoGroups] = useState([]);
    const [loading, setLoading]       = useState(true);
    const [subName, setSubName]       = useState("");

    const editor = useEditor({
        extensions: [StarterKit],
        content: "",
    });

    const load = async () => {
        try {
            const [projRes, todoRes] = await Promise.all([
                getFromApi(`api/projects/${id}`),
                getFromApi("api/todo"),
            ]);
            setProject(projRes.data || null);
            setLinkedTodo(projRes.data?.linkedTodo || null);
            setAllTodoGroups(todoRes.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const handleLog = async (e) => {
        e.preventDefault();
        if (!editor) return;
        const content = editor.getHTML();
        if (content === "<p></p>") return;
        await postToApi(`api/projects/${id}/log`, {log: content});
        editor.commands.clearContent();
        load();
    };

    const handleDelete = async (entryId) => {
        await deleteFromApi(`api/projects/${id}/log/${entryId}`);
        load();
    };

    const handleAddSub = async (e) => {
        e.preventDefault();
        if (!subName.trim()) return;
        await postToApi("api/projects", {name: subName.trim(), parent_id: id});
        setSubName("");
        load();
    };

    const handleRenameSub = async (subId, newName) => {
        await patchToApi(`api/projects/${subId}`, {name: newName});
        load();
    };

    const handleDeleteSub = async (subId) => {
        await deleteFromApi(`api/projects/${subId}`);
        load();
    };

    if (loading) return <div className="text-muted py-4 text-center">Loading...</div>;
    if (!project) return <div className="py-4">Project not found.</div>;

    const {details, log, parent, children} = project;
    const projectName = details?.[0]?.name || "Project";
    const parentName  = parent?.[0]?.name;
    const parentId    = parent?.[0]?._id;

    const groupedByDate = (log || []).reduce((acc, entry) => {
        const date = entry.timestamp?.split("T")[0] || "Unknown";
        (acc[date] = acc[date] || []).push(entry);
        return acc;
    }, {});

    return (
        <div>
            <div className="breadcrumb-nav mb-1">
                <Link to="/project-log">Project Log</Link>
                {parentName && <> / <Link to={`/project-log/${parentId}`}>{parentName}</Link></>}
                {" "}/ {projectName}
            </div>
            <div className="page-header">
                <h4><i className="bi bi-folder2-open me-2 text-warning"/>{projectName}</h4>
            </div>

            <div className="panel mb-4">
                <form onSubmit={handleLog}>
                    <label className="form-label small fw-medium">Add log entry</label>
                    <MenuBar editor={editor}/>
                    <EditorContent editor={editor} className="tiptap-editor"/>
                    <button type="submit" className="btn btn-dark btn-sm mt-2">
                        <i className="bi bi-plus-lg me-1"/>Log
                    </button>
                </form>
            </div>

            <LinkedTodo
                folderId={id}
                todo={linkedTodo}
                allTodoGroups={allTodoGroups}
                onRefresh={load}
                linkApiPath={`api/projects/${id}/link-todo`}
            />

            <div className="panel mb-4">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-medium small"><i className="bi bi-folder2 me-1 text-warning"/>Sub-projects</span>
                </div>
                {children?.length > 0 && (
                    <div className="mb-3 panel p-0" style={{overflow:"hidden"}}>
                        {children.map((c, i) => (
                            <div key={c._id} className={`d-flex align-items-center justify-content-between px-3 py-2${i < children.length - 1 ? " border-bottom" : ""}`}>
                                <div className="d-flex align-items-center gap-2">
                                    <i className="bi bi-folder text-secondary small"/>
                                    <InlineRename value={c.name} onSave={name => handleRenameSub(c._id, name)} className="small"/>
                                    <span className="small text-muted">{c.entryCount || 0} entries</span>
                                </div>
                                <div className="d-flex gap-2">
                                    <Link to={`/project-log/${c._id}`} className="btn btn-sm btn-outline-secondary">Open</Link>
                                    <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => handleDeleteSub(c._id)}>
                                        <i className="bi bi-trash"/>
                                    </ConfirmButton>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <form onSubmit={handleAddSub} className="d-flex gap-2">
                    <input className="form-control form-control-sm" placeholder="New sub-project name..." value={subName} onChange={e => setSubName(e.target.value)}/>
                    <button type="submit" className="btn btn-sm btn-dark text-nowrap"><i className="bi bi-plus-lg me-1"/>Add</button>
                </form>
            </div>

            {Object.entries(groupedByDate).reverse().map(([date, entries]) => (
                <div key={date} className="mb-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="bi bi-calendar3 text-muted small"/>
                        <span className="fw-semibold small text-muted">{date}</span>
                    </div>
                    {entries.map(entry => (
                        <div key={entry._id} className="panel mb-2 d-flex gap-3 align-items-start" style={{padding:"1rem",borderLeft:"3px solid #f59e0b"}}>
                            <div className="flex-grow-1" dangerouslySetInnerHTML={{__html: entry.log}}/>
                            <ConfirmButton className="btn btn-sm btn-outline-danger flex-shrink-0" onConfirm={() => handleDelete(entry._id)}>
                                <i className="bi bi-trash"/>
                            </ConfirmButton>
                        </div>
                    ))}
                </div>
            ))}
            {!log?.length && (
                <div className="empty-state">
                    <i className="bi bi-journal-plus"/>
                    <div>No log entries yet.</div>
                </div>
            )}
        </div>
    );
}
